// react components
import React, {
    useState,
    useEffect,
} from 'react'
import {
    Elements,
} from '@stripe/react-stripe-js'
import {
    useDispatch,
    useSelector,
} from 'react-redux'

// components
import {
    LinkHelperSite,
} from 'components'

// data
import {
    defaultReduxState,
    reduxSalesAddToCartSite,
    reduxSalesCartToggleSite,
    reduxSalesTicketSite,
} from 'data'

// pages
import {
    BillingHelper816Site,
    EditBlockSite,
    ParticipantHelper816Site,
    ReviewHelper816Site,
    StripeHelper816Site,
    VariantHelper816Site,
} from 'pages'

// serializers
import {
    MainStyleSerializer,
    PortfolioPageContentListSiteSerializer,
    Template816SiteSerializer,
} from 'serializers/site'

// props
type Sales816SiteProps = {
    blockId: string
    content: Template816SiteSerializer | undefined
    isEditHovered: boolean
    mainParentId?: number
    object: PortfolioPageContentListSiteSerializer
    parentArray?: number[]
    parentStyles?: any
    styles: any
    stylesEdit: MainStyleSerializer | undefined
}

// main
export const Sales816Site: React.FC<Sales816SiteProps> = React.memo(({
    blockId,
    content,
    isEditHovered,
    mainParentId,
    object,
    parentArray,
    parentStyles,
    styles,
    stylesEdit,
}) => {

    const dispatch = useDispatch()
    const reduxCacheSite = useSelector((state: defaultReduxState) => state.reduxCacheSite)
    const reduxModalSite = useSelector((state: defaultReduxState) => state.reduxModalSite)
    const reduxSalesSite = useSelector((state: defaultReduxState) => state.reduxSalesSite)
    const reduxText = useSelector((state: defaultReduxState) => state.reduxText.data)

    const [step, setStep] = useState<number>(1)
    const [isTryingToExceedLimit, setIsTryingToExceedLimit] = useState<boolean>(false)

    // Check if this is portfolio 696 (TOHU BOHU) - uses 3-step cart flow
    const isCartFlow = reduxCacheSite.portfolio?.id === 696
    const maxSteps = isCartFlow ? 3 : 4

    const checkIfTryingToExceedLimit = () => {
        if (!content?.count_participant_available) return false
        const currentTotal = getTotalTickets()
        const isAtLimit = currentTotal >= content.count_participant_available
        setIsTryingToExceedLimit(isAtLimit)
        return isAtLimit
    }

    // Check limit status whenever ticket data changes
    useEffect(() => {
        checkIfTryingToExceedLimit()
    }, [reduxSalesSite.ticketData])

    const deviceType = reduxModalSite.deviceType

    if (!content) return null

    // Helper function to calculate total tickets
    const getTotalTickets = () => {
        if (!reduxSalesSite.ticketData?.variants) return 0
        return Object.values(reduxSalesSite.ticketData.variants).reduce((total, variant) => {
            const participantQty = variant.variant?.quantity_of_participant_to_register || 1
            return total + ((variant.qty || 0) * participantQty)
        }, 0)
    }

    // Function to handle adding tickets to cart (for portfolio 696)
    const handleAddToCart = () => {
        if (!reduxSalesSite.ticketData?.variants || !content) return

        // Create form fields label mapping
        const formFieldsMapping: { [key: string]: string } = {}
        if (content.form_fields && content.form_fields.length > 0) {
            content.form_fields.forEach(field => {
                if (field.name && field.label) {
                    formFieldsMapping[field.name] = field.label
                }
            })
        } else {
            // Default labels for first_name and last_name
            formFieldsMapping['first_name'] = reduxText[8744] || 'Prénom'
            formFieldsMapping['last_name'] = reduxText[8745] || 'Nom'
        }

        // Add each ticket separately to the cart (matching the original booking system)
        Object.values(reduxSalesSite.ticketData.variants).forEach((variantData) => {
            if (variantData.qty > 0) {
                // Loop through each ticket quantity and add individually
                Array.from({ length: variantData.qty }).forEach((_, index) => {
                    // Get the participant data for this specific ticket
                    const participantData = variantData.participants?.[index]

                    // Dispatch each ticket separately to cart with quantity 1
                    dispatch(reduxSalesAddToCartSite(
                        1, // Each ticket is added separately with quantity 1
                        variantData.variant.is_free_pricing ? (variantData.freeUnitPrice || 0) : variantData.variant.unit_price,
                        content, // product (the event/ticket item)
                        variantData.variant,
                        content.absolute_site_url || '',
                        reduxCacheSite.portfolio!,
                        undefined, // no package options for tickets
                        undefined, // currency
                        participantData, // Individual participant data for this ticket
                        formFieldsMapping // Label mapping for displaying in cart
                    ))
                })
            }
        })

        // Reset ticket data after adding to cart
        dispatch(reduxSalesTicketSite({
            variants: {},
            contact_info: {},
        }))

        // Reset to step 1 to allow adding more tickets
        setStep(1)

        // Open cart popup
        dispatch(reduxSalesCartToggleSite(true))
    }

    let isNextValid = false
    if (step === 1) {
        if (reduxSalesSite.ticketData?.variants && Object.values(reduxSalesSite.ticketData.variants).filter(obj => obj.qty > 0).length > 0) {
            // Additional check: ensure total tickets don't exceed the limit
            if (content.count_participant_available) {
                const totalTickets = getTotalTickets()
                isNextValid = totalTickets <= content.count_participant_available
            } else {
                isNextValid = true
            }
        }
    } else if (step === 2) {
        if (content.form_fields?.length! > 0) {
            const filteredFields = content.form_fields?.filter(field => field.required === true)
            let totalParticipants = 0
            const fieldCounts: { [key: string]: number } = {}
            filteredFields?.forEach(field => {
                fieldCounts[field.name!] = 0
            })
            reduxSalesSite.ticketData?.variants && Object.values(reduxSalesSite.ticketData.variants).filter(obj => obj.qty > 0).map((val) => {
                [...Array(val.qty)].map((_) => {
                    totalParticipants += 1
                })
                val.participants && Object.values(val.participants).map((val2) => {
                    filteredFields?.forEach(field => {
                        const fieldName = field.name
                        if (val2[fieldName!]) {
                            fieldCounts[fieldName!] += 1
                        }
                    })
                })
            })
            const totalFilledFields = Object.values(fieldCounts).reduce(
                (acc, count) => acc + count,
                0
            )
            isNextValid = totalParticipants * filteredFields?.length! === totalFilledFields
        } else {
            let totalParticipants = 0
            let totalParticipantsfirst_name = 0
            let totalParticipantslast_name = 0
            reduxSalesSite.ticketData?.variants && Object.values(reduxSalesSite.ticketData.variants).filter(obj => obj.qty > 0 && !obj.variant?.ticket_info_not_needed).map((val) => {
                [...Array(val.qty)].map((_) => {
                    totalParticipants += 1
                })
                val.participants && Object.values(val.participants).map((val2) => {
                    if (val2.first_name) totalParticipantsfirst_name += 1
                    if (val2.last_name) totalParticipantslast_name += 1
                })
            })
            isNextValid = (totalParticipants * 2) === (totalParticipantsfirst_name + totalParticipantslast_name)
        }
    } else if (step === 3 && !isCartFlow) {
        // Step 3 only exists for non-cart flow (billing info)
        if (content?.form_fields_payment && content.form_fields_payment.length > 0) {
            // Check that all form_fields_payment fields have values
            const requiredFields = content.form_fields_payment.filter(field => field.required === true)
            if (requiredFields.length > 0) {
                isNextValid = requiredFields.every(field =>
                    reduxSalesSite.ticketData?.contact_info?.[field.name!] &&
                    reduxSalesSite.ticketData?.contact_info?.[field.name!] !== ''
                )
            } else {
                // If no required fields, check that all fields have values
                isNextValid = content.form_fields_payment.every(field =>
                    reduxSalesSite.ticketData?.contact_info?.[field.name!] &&
                    reduxSalesSite.ticketData?.contact_info?.[field.name!] !== ''
                )
            }
        } else if (object.id === 308983) {  // TODO dynamic
            isNextValid = Boolean(reduxSalesSite.ticketData?.contact_info?.email && reduxSalesSite.ticketData?.contact_info?.first_name && reduxSalesSite.ticketData?.contact_info?.last_name && reduxSalesSite.ticketData?.contact_info?.agree)
        } else {
            isNextValid = Boolean(reduxSalesSite.ticketData?.contact_info?.email && reduxSalesSite.ticketData?.contact_info?.first_name && reduxSalesSite.ticketData?.contact_info?.last_name)
        }
    }

    return (
        <div
            id={blockId}
            className={`template-block-816${isEditHovered ? ' is-edit-hovered' : ''} ${deviceType}`}
        >
            <div
                id='anchorToTickets'
                className={`t-816-block ${deviceType}`}
            >
                <div className={`t-816-navsteps${isCartFlow ? ' three-steps' : ''} ${deviceType}`}>
                    <button
                        className={`t-816-navstep${step === 1 ? ' current' : ''} ${deviceType}`}
                        onClick={() => setStep(1)}
                        type='button'
                    >
                        <i className={`t-816-icon mo-new-icon-layout-792 ${deviceType}`} />
                        <span className={`t-816-navtext ${deviceType}`}>{reduxText[8735]}</span>
                    </button>
                    <button
                        className={`t-816-navstep${step === 2 ? ' current' : ''} ${deviceType}`}
                        disabled={step < 2}
                        onClick={() => setStep(2)}
                        type='button'
                    >
                        <i className={`t-816-icon mo-new-icon-user-solid ${deviceType}`} />
                        <span className={`t-816-navtext ${deviceType}`}>{reduxText[8736]}</span>
                    </button>
                    {!isCartFlow && (
                        <button
                            className={`t-816-navstep${step === 3 ? ' current' : ''} ${deviceType}`}
                            disabled={step < 3}
                            onClick={() => setStep(3)}
                            type='button'
                        >
                            <i className={`t-816-icon mo-new-icon-list-ul-solid ${deviceType}`} />
                            <span className={`t-816-navtext ${deviceType}`}>{reduxText[8737]}</span>
                        </button>
                    )}
                    <button
                        className={`t-816-navstep${step === (isCartFlow ? 3 : 4) ? ' current' : ''} ${deviceType}`}
                        disabled={step < (isCartFlow ? 3 : 4)}
                        type='button'
                    >
                        <i className={`t-816-icon mo-new-icon-check-circle-solid ${deviceType}`} />
                        <span className={`t-816-navtext ${deviceType}`}>{isCartFlow ? 'Récapitulatif' : reduxText[8738]}</span>
                    </button>
                </div>
                {step === 1 && (
                    <div className={`t-816-step1 ${deviceType}`}>
                        {content?.count_participant_available && (getTotalTickets() >= content.count_participant_available || isTryingToExceedLimit) && (
                            <div className={`t-816-ticket-limit ${deviceType} critical`}>
                                <span className={`t-816-ticket-limit-text ${deviceType}`}>
                                    Il y a un maximum de {content.count_participant_available} places disponibles.
                                </span>
                            </div>
                        )}
                        <div className={`t-816-variants ${deviceType}`}>
                            {content?.variants?.map((val, i) => (
                                <VariantHelper816Site
                                    key={val.id}
                                    index={i}
                                    object={val}
                                    onLimitCheck={(isTryingToExceed) => setIsTryingToExceedLimit(isTryingToExceed)}
                                    product={content}
                                />
                            ))}
                        </div>
                        <div className={`t-816-total ${deviceType}`}>
                            <span className={`t-816-total-text ${deviceType}`}>{reduxText[8739]}</span>
                            <span className={`t-816-total-amount ${deviceType}`}>{Number(reduxSalesSite.ticketData?.totalValue || 0)} {reduxCacheSite.portfolio?.currency?.symbol}</span>
                        </div>
                    </div>
                )}
                {step === 2 && reduxSalesSite.ticketData?.variants && (
                    <div className={`t-816-step2 ${deviceType}`}>
                        {Object.values(reduxSalesSite.ticketData.variants).filter(obj => obj.qty > 0 && !obj.variant?.ticket_info_not_needed).map((val) => (
                            <div
                                key={val.variant.id}
                                className={`t-816-variant-wrap ${deviceType}`}
                            >
                                <span className={`t-816-variant-name ${deviceType}`}>{val.variant.name}</span>
                                {[...Array(val.qty)].map((_, index) => (
                                    <ParticipantHelper816Site
                                        key={index}
                                        content={content}
                                        index={index}
                                        object={val}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                )}
                {step === 3 && !isCartFlow && (
                    <div className={`t-816-step3 ${deviceType}`}>
                        <BillingHelper816Site
                            content={content}
                            objectId={object.id!}
                        />
                    </div>
                )}
                {step === 3 && isCartFlow && (
                    <div className={`t-816-step3 ${deviceType}`}>
                        <ReviewHelper816Site />
                        <div className={`t-816-total ${deviceType}`}>
                            <span className={`t-816-total-text ${deviceType}`}>{reduxText[8740]}</span>
                            <span className={`t-816-total-amount ${deviceType}`}>{Number(reduxSalesSite.ticketData?.totalValue || 0)} {reduxCacheSite.portfolio?.currency?.symbol}</span>
                        </div>
                    </div>
                )}
                {step === 4 && !isCartFlow && (
                    <div className={`t-816-step4 ${deviceType}`}>
                        <ReviewHelper816Site />
                        <div className={`t-816-total ${deviceType}`}>
                            <span className={`t-816-total-text ${deviceType}`}>{reduxText[8740]}</span>
                            <span className={`t-816-total-amount ${deviceType}`}>{Number(reduxSalesSite.ticketData?.totalValue || 0)} {reduxCacheSite.portfolio?.currency?.symbol}</span>
                        </div>
                    </div>
                )}
            </div>
            <div className={`t-816-button-next-wrap ${deviceType}`}>
                {step > 1 && (
                    <LinkHelperSite
                        action={undefined}
                        className={`t-816-button-previous ${deviceType}`}
                        onClick={() => setStep(step - 1)}
                        to={undefined}
                    >
                        <i className={`t-816-icon mo-new-icon-chevron-left-solid ${deviceType}`} />
                        <span>{reduxText[8741]}</span>
                    </LinkHelperSite>
                )}
                {step < maxSteps && (
                    <LinkHelperSite
                        action={undefined}
                        className={`t-816-button-next${isNextValid ? '' : ' disabled'} ${deviceType}`}
                        onClick={() => setStep(step + 1)}
                        to={undefined}
                    >
                        <span>{reduxText[8742]}</span>
                        <i className={`t-816-icon mo-new-icon-chevron-right-solid ${deviceType}`} />
                    </LinkHelperSite>
                )}
                {step === maxSteps && isCartFlow && (
                    <LinkHelperSite
                        action={undefined}
                        className={`t-816-button-next ${deviceType}`}
                        onClick={handleAddToCart}
                        to={undefined}
                    >
                        <i className={`t-816-icon mo-new-icon-shopping-cart-solid ${deviceType}`} />
                        <span>Ajouter au panier</span>
                    </LinkHelperSite>
                )}
                {step === 4 && !isCartFlow && (
                    <Elements stripe={reduxModalSite.stripePromise}>
                        <StripeHelper816Site />
                    </Elements>
                )}
            </div>
            <EditBlockSite
                mainParentId={mainParentId || object.id!}
                object={object}
                parentArray={parentArray}
                parentStyles={parentStyles}
            />
        </div>
    )
})
